import type { InvoiceMatchGap } from '@erp/rpc';
import { APInvoiceStatus } from '@erp/shared-types';

export type ApprovalBlock =
  | { type: 'no_lines' }
  | { type: 'gaps'; gaps: InvoiceMatchGap[] }
  | {
      type: 'incomplete_summary';
      unmatched: number;
      tentative: number;
      total: number;
    }
  | { type: 'supplier' }
  | { type: 'invoice_number' };

type MatchSummary = {
  total: number;
  confirmed: number;
  tentative: number;
  unmatched: number;
};

/**
 * Everything that stops Accept Matches. Match gaps come from the server
 * (`matchGaps`). When that list has not been loaded, the match summary is
 * the fallback so a partial invoice is not treated as ready.
 */
export function invoiceApprovalBlocks(invoice: {
  supplierId?: string | null;
  invoiceNumber?: string | null;
  lineItemMatchSummary?: MatchSummary | null;
  matchGaps?: InvoiceMatchGap[] | null;
}): ApprovalBlock[] {
  const blocks: ApprovalBlock[] = [];
  const summary = invoice.lineItemMatchSummary ?? null;
  const gaps = invoice.matchGaps;

  if (summary != null && summary.total === 0) {
    blocks.push({ type: 'no_lines' });
  } else if (gaps != null && gaps.length > 0) {
    blocks.push({ type: 'gaps', gaps });
  } else if (
    gaps == null &&
    summary != null &&
    !isSummaryFullyMatched(summary)
  ) {
    blocks.push({
      type: 'incomplete_summary',
      unmatched: summary.unmatched,
      tentative: summary.tentative,
      total: summary.total,
    });
  }

  if (!invoice.supplierId) blocks.push({ type: 'supplier' });
  if (!invoice.invoiceNumber) blocks.push({ type: 'invoice_number' });
  return blocks;
}

type AcceptCandidate = {
  status: string;
  supplierId?: string | null;
  invoiceNumber?: string | null;
  lineItemMatchSummary?: MatchSummary | null;
  matchGaps?: InvoiceMatchGap[] | null;
};

/** Draft or matched invoices split into ones Accept can take and ones it cannot. */
export function splitInvoicesForAccept<T extends AcceptCandidate>(
  invoices: readonly T[],
): {
  acceptable: T[];
  blocked: T[];
} {
  const acceptable: T[] = [];
  const blocked: T[] = [];
  for (const invoice of invoices) {
    if (
      invoice.status !== APInvoiceStatus.DRAFT &&
      invoice.status !== APInvoiceStatus.MATCHED
    ) {
      continue;
    }
    if (invoiceApprovalBlocks(invoice).length === 0) {
      acceptable.push(invoice);
    } else {
      blocked.push(invoice);
    }
  }
  return { acceptable, blocked };
}

export function approvalBlocksIncludeMatchGaps(
  blocks: readonly ApprovalBlock[],
): boolean {
  return blocks.some(
    (block) =>
      block.type === 'no_lines' ||
      block.type === 'gaps' ||
      block.type === 'incomplete_summary',
  );
}

function isSummaryFullyMatched(summary: MatchSummary): boolean {
  return (
    summary.total > 0 &&
    summary.confirmed === summary.total &&
    summary.tentative === 0 &&
    summary.unmatched === 0
  );
}
